import { useCallback } from 'react';
import { useRelayEnvironment, type GraphQLTaggedNode } from 'react-relay';
import { type DocumentNode } from 'graphql/language/ast';
import { useApolloClient } from '@apollo/react-hooks';
import { type IEnvironment, type ReaderSelection } from 'relay-runtime';

type RelayUseWriteFragmentInteropParams = { fragmentSpec: GraphQLTaggedNode };
type ApolloUseWriteFragmentInteropParams = { fragmentSpec: DocumentNode };

export type UseWriteFragmentInteropParams = {
	fragmentSpec: DocumentNode | GraphQLTaggedNode;
};

export type FragmentWriterInteropParams<TData> = { fragmentId: string; data: TData };
export type FragmentWriterInterop<TData> = (params: FragmentWriterInteropParams<TData>) => void;

export function __test_only_useWriteFragmentInterop__Relay<TData>(
	params: RelayUseWriteFragmentInteropParams,
): FragmentWriterInterop<TData> {
	const { fragmentSpec } = params;
	const env = useRelayEnvironment();

	return useCallback(
		({ data, fragmentId }) => {
			writeFragmentShim(env, fragmentSpec, fragmentId, data);
		},
		[env, fragmentSpec],
	);
}

export function __test_only_useWriteFragmentInterop__Apollo<TData>(
	params: ApolloUseWriteFragmentInteropParams,
): FragmentWriterInterop<TData> {
	const { fragmentSpec } = params;
	const client = useApolloClient();

	return useCallback(
		({ data, fragmentId }) => {
			client.writeFragment<TData>({
				id: fragmentId,
				fragment: fragmentSpec,
				data,
			});
		},
		[client, fragmentSpec],
	);
}

export function useWriteFragmentInterop<TData>(
	params: UseWriteFragmentInteropParams,
): FragmentWriterInterop<TData> {
	return process.env.BUILD_VARIANT === 'relay'
		? __test_only_useWriteFragmentInterop__Relay(
				params as UseWriteFragmentInteropParams & RelayUseWriteFragmentInteropParams,
			)
		: __test_only_useWriteFragmentInterop__Apollo(
				params as UseWriteFragmentInteropParams & ApolloUseWriteFragmentInteropParams,
			);
}

function apolloFragmentIdToRelayFragmentId(apolloFragmentId: string) {
	return apolloFragmentId.replace(/^\$ROOT_QUERY\./, 'client:root:');
}

/**
 * Shim function that mimics Apollo's `writeFragment` using Relay
 */
function writeFragmentShim<TData>(
	environment: IEnvironment,
	fragment: GraphQLTaggedNode,
	fragmentId: string,
	data: TData,
): void {
	if (!data) {
		return;
	}

	const relayFragmentId = apolloFragmentIdToRelayFragmentId(fragmentId);

	// Use commitUpdate to write to the store
	environment.commitUpdate((storeProxy) => {
		const record = storeProxy.get(relayFragmentId);
		if (!record) {
			return;
		}

		// Define a function to write the fields specified in the fragment
		const writeFields = (
			recordId: string,
			fields: readonly ReaderSelection[],
			writeData: any,
		): void => {
			const targetRecord = storeProxy.get(recordId);
			if (!targetRecord || !writeData) {
				return;
			}

			for (const field of fields) {
				if (field.kind === 'LinkedField') {
					// For linked fields, we need to handle nested data
					const linkedData = writeData[field.name];
					if (linkedData) {
						const linkedRecord = targetRecord.getLinkedRecord(field.name);
						if (linkedRecord) {
							writeFields(linkedRecord.getDataID(), field.selections, linkedData);
						}
					}
				} else if (field.kind === 'ScalarField') {
					if (writeData[field.name] !== undefined) {
						targetRecord.setValue(writeData[field.name], field.name);
					}
				} else {
					throw new Error('Unsupported field');
				}
			}
		};

		const writerFragment = fragment as any;
		const topLevelFields = writerFragment?.selections;

		if (topLevelFields) {
			writeFields(relayFragmentId, topLevelFields ?? [], data);
		}
	});
}
